/**
 * PURPOSE: Estado de la importación SISDEU (FTR-0048..0050) para un evento/deporte y, opcionalmente, una institución.
 * RESPONSIBILITIES: { loading, status, reload }. Mientras carga, y ante cualquier error, `status` es TODO apagado
 *   (fail closed, misma regla que el servicio): la interfaz no ofrece la importación sin una confirmación del backend.
 *   `enabled` = false evita la consulta (p. ej. un modal cerrado). Sin caché entre consumidores: el estado depende del
 *   contexto (institución vinculada o no) y cambia tras importar o vincular.
 * COLLABORATORS: services/sisdeuImportService.js, SisdeuImportPanel.jsx, InstitucionesArea.jsx, CategoriaSection.jsx,
 *   ModalConfigPrimeraFase.jsx, Regionales.jsx, Regionales2.jsx.
 * ROUTE: winscore/src/hooks/useSisdeuImportStatus.js
 */
import { useCallback, useEffect, useState } from "react";

import { SISDEU_IMPORT_STATUS_OFF, getSisdeuImportStatus } from "../services/sisdeuImportService.js";

export const useSisdeuImportStatus = ({ idevent, idsport, idinstitution = null, enabled = true }) => {
  const contextKey = `${idevent ?? ""}|${idsport ?? ""}|${idinstitution ?? ""}`;
  const [state, setState] = useState({ loading: Boolean(enabled), status: SISDEU_IMPORT_STATUS_OFF, contextKey: null });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let active = true;
    if (!enabled || !idevent || !idsport) {
      setState({ loading: false, status: SISDEU_IMPORT_STATUS_OFF, contextKey });
      return undefined;
    }
    // Contexto nuevo: todo apagado hasta la respuesta. Recarga del mismo contexto (tras importar): se conserva el
    // estado anterior mientras llega el nuevo, para que la interfaz no parpadee ni pierda el resultado mostrado.
    setState((current) =>
      current.contextKey === contextKey ? current : { loading: true, status: SISDEU_IMPORT_STATUS_OFF, contextKey }
    );
    getSisdeuImportStatus({ idevent, idsport, idinstitution }).then((status) => {
      if (active) setState({ loading: false, status, contextKey });
    });
    return () => {
      active = false;
    };
  }, [contextKey, idevent, idsport, idinstitution, enabled, revision]);

  const reload = useCallback(() => setRevision((value) => value + 1), []);

  if (!enabled || !idevent || !idsport) return { loading: false, status: SISDEU_IMPORT_STATUS_OFF, reload };
  // El render en que cambia el contexto, antes de que el efecto reinicie el estado, también es "todo apagado".
  const stale = state.contextKey !== contextKey;
  return { loading: stale || state.loading, status: stale ? SISDEU_IMPORT_STATUS_OFF : state.status, reload };
};

export default useSisdeuImportStatus;
